import React, { useEffect, useRef, useState } from "react";
import { Calendar, ConfigProvider, Modal, Space, Switch, DatePicker, Spin } from "antd";
import en_GB from "antd/locale/en_GB";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import infoImage from "@images/info-icon.svg";
import "@styles/styles-Calendar.css";
import { getDateString } from "@utils/calendar_utils";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { PredictionModal } from "@components/Common/PredictionModal/PredictionModal";
import {
  deletePracticePlanForDate,
  getCalendarDayV2,
  getCalendarMonthV2,
} from "@store/actions/dualcalendar.actions";
import { getCalendarDayTags } from "@store/actions/team.actions";
import { DateCellContentV2 } from "./DateCellContentV2";
import { DateInfoPopoutV2 } from "./DateInfoPopoutV2";
import { HelpPopout } from "./HelpPopout";
import { deleteDayTarget, getDayTargetDay } from "@store/actions/dayTargets.actions";
import { errorText } from "@utils/dayTargets.mjs";

const DATE_FORMAT = "MM/DD/YYYY";
const TAG_DATE_FORMAT = "YYYY-MM-DD";

/**
 * v2 of the dual-calendar month grid, backed by /calendar/month and /calendar/day
 * instead of /v3/year. Rendered twice by OverviewStatusTracker — calendarId 1 is
 * last month, 2 is the current month — the only dual-calendar implementation,
 * which are byte-identical apart from those two facts.
 *
 * Clicking a past day opens the day-detail popout; clicking a future day opens the
 * practice-plan creator, or the prediction detail when a plan is already saved.
 */
export const CalendarViewV2 = ({ calendarId = 1 }) => {
  const dispatch = useDispatch();
  const {
    submitClicked,
    filters: {
      playersFilter,
      positionsFilter,
      playerGroupsFilter,
      playerStatusesFilter,
      sessionsFilter,
      flagTypesFilter,
    },
  } = useSelector(({ dualCalendar }) => dualCalendar);
  const { month: data, monthLoading, day: dayInfo, dayLoading } = useSelector(
    ({ dualCalendar }) => dualCalendar.v2[calendarId]
  );
  const { drills } = useSelector(({ team }) => team);

  const [selectedDate, setSelectedDate] = useState(
    calendarId === 1 ? dayjs().subtract(1, "month") : dayjs()
  );
  const [showHelp, setShowHelp] = useState(false);
  const [showGameAndTraining, setShowGameAndTraining] = useState(false);
  const [showPredictionDialog, setShowPredictionDialog] = useState(false);
  const [predictionDialogDate, setPredictionDialogDate] = useState(null);
  const [showDateInfoPopout, setShowDateInfoPopout] = useState(false);
  // The method of the clicked day's day target.
  const [dayTargetMethod, setDayTargetMethod] = useState(null);
  const dayTargetClick = useRef(0);

  const getData = (date) => {
    dispatch(getCalendarMonthV2({
      calendarId,
      start_date: date.startOf("month").format(DATE_FORMAT),
      end_date: date.endOf("month").format(DATE_FORMAT),
      player_ids: playersFilter,
      positions: positionsFilter,
      player_groups: playerGroupsFilter,
      player_statuses: playerStatusesFilter,
      sessions: sessionsFilter,
      flag_types: flagTypesFilter,
    }));
    // Day tags only decorate the cells, so a failure here must not surface over an
    // otherwise-loaded calendar — but it still needs handling, as the action rethrows.
    dispatch(getCalendarDayTags(
      date.startOf("month").format(TAG_DATE_FORMAT),
      date.endOf("month").format(TAG_DATE_FORMAT)
    )).catch((e) => console.error("Error loading calendar day tags", e));
  };

  useEffect(() => {
    getData(selectedDate);
  }, []);

  useEffect(() => {
    if (submitClicked) {
      getData(selectedDate);
    }
  }, [submitClicked]);

  // Mirrors v1's isFutureDate: a clickable future day is one present in the data
  // that is after today, or today itself with no training recorded yet. The slim
  // month cell reports has_training where v1 read total_time.
  const isFutureDate = (date) => {
    const dateString = getDateString(date);
    const todayString = dayjs().format(TAG_DATE_FORMAT);
    const cell = data?.[dateString];
    if (!cell) return false;
    return dateString > todayString || (dateString === todayString && !cell.has_training);
  };

  const openPredictionDialog = (dateString) => {
    setPredictionDialogDate(dateString);
    setShowPredictionDialog(true);
  };

  // Mount the popout first so the click registers immediately, then let the day
  // request fill it in. drillsOverride carries a saved practice plan's drills (which
  // have durations) in place of the empty actual-drills a future date returns.
  const openDayInfoPopout = (dateString, drillsOverride = null) => {
    setShowDateInfoPopout(true);
    dispatch(getCalendarDayV2({
      calendarId,
      date: dayjs(dateString).format(DATE_FORMAT),
      drillsOverride,
      player_ids: playersFilter,
      positions: positionsFilter,
      player_groups: playerGroupsFilter,
      player_statuses: playerStatusesFilter,
      sessions: sessionsFilter,
      flag_types: flagTypesFilter,
    }));
  };

  // One call answers which method built the day: a saved day opens the popout, a new day the
  // dialog with both tabs. A later click wins over a response that arrives late.
  const openDayTarget = async (dateString, click) => {
    let day;
    try {
      day = await dispatch(getDayTargetDay(dateString));
    } catch (e) {
      if (click === dayTargetClick.current) message.error(errorText(e, "Error loading the day target"));
      return;
    }
    if (click !== dayTargetClick.current) return;
    setDayTargetMethod(day?.method ?? null);
    if (day?.method === "practice_plan") {
      openDayInfoPopout(dateString, (day.practice_plan ?? []).map(({ drill_id, name, duration }) => ({ id: drill_id, name, duration })));
    } else if (day?.method === "fixed_values") {
      openDayInfoPopout(dateString);
    } else {
      openPredictionDialog(dateString);
    }
  };

  // A save or delete from the dialog also closes the popout it may have come from.
  const handlePredictionSuccess = () => {
    setShowDateInfoPopout(false);
    setDayTargetMethod(null);
    getData(selectedDate);
  };

  const handleClickDateCell = async (date) => {
    const click = ++dayTargetClick.current;
    const dateString = getDateString(date);
    setDayTargetMethod(null);

    // A past day with a day target and no training still has its target, so it opens like a future day.
    const cell = data?.[dateString];
    if (!isFutureDate(date) && !(cell?.is_prediction && !cell?.has_training)) {
      if (cell) openDayInfoPopout(dateString);
      return;
    }

    await openDayTarget(dateString, click);
  };

  const handleDeletePracticePlan = async () => {
    const dateString = dayInfo?.date;
    if (!dateString) return;
    setShowDateInfoPopout(false);
    if (await dispatch(deletePracticePlanForDate(dateString))) {
      getData(selectedDate);
    }
  };

  // "Delete" in the popout, for either method: ask first, then delete and reload the month.
  const handleDeleteDayTarget = () => {
    const dateString = dayInfo?.date;
    if (!dateString) return;
    Modal.confirm({
      centered: true,
      title: "Delete this day target?",
      content: `This deletes the day target for ${dayjs(dateString).format("ddd, MMM D")}. You cannot undo this.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setShowDateInfoPopout(false);
        if (dayTargetMethod === "fixed_values") {
          try {
            await dispatch(deleteDayTarget(dateString));
            message.success("Day target deleted");
            getData(selectedDate);
          } catch (e) {
            message.error(errorText(e, "Error deleting the day target"));
          }
        } else if (await dispatch(deletePracticePlanForDate(dateString))) {
          getData(selectedDate);
        }
      },
    });
  };

  const cellRender = (value) => {
    if (!data) return null;
    return (
      <DateCellContentV2
        date={value}
        info={data[getDateString(value)]}
        onClick={() => handleClickDateCell(value)}
        showGameAndTraining={showGameAndTraining}
      />
    );
  };

  const handleDateChange = (date) => {
    // Only refetch when the picker actually moved to a different month.
    if (selectedDate && date.month() === selectedDate.month() && date.year() === selectedDate.year()) {
      return;
    }
    getData(date);
  };

  const headerRender = ({ onChange }) => (
    <div className="calendar-header">
      <DatePicker
        value={selectedDate}
        format={"MMM YYYY"}
        picker="month"
        onChange={(date) => {
          setSelectedDate(date);
          onChange(date);
        }}
        allowClear={false}
      />
      <div className="calender-gt-container">
        {/* Toggling between showing game/training or score */}
        <Switch
          checkedChildren="G/T"
          unCheckedChildren="Score"
          defaultChecked={!showGameAndTraining}
          onChange={() => setShowGameAndTraining(!showGameAndTraining)}
        />

        <img
          src={infoImage}
          alt="info-img"
          style={{ cursor: "pointer", width: "20px", height: "20px" }}
          onClick={() => setShowHelp(true)}
          className="info-img"
        />
      </div>
    </div>
  );

  return (
    <>
      {showDateInfoPopout && (
        <div style={{ position: "absolute", width: "100%", height: "100%", top: 0, left: 0 }}>
          <DateInfoPopoutV2
            showInfoPopout={showDateInfoPopout}
            setShowInfoPopout={setShowDateInfoPopout}
            info={dayInfo}
            loading={dayLoading}
            openPredictionDialog={openPredictionDialog}
            handleDeletePracticePlan={handleDeletePracticePlan}
            showHelp={() => { setShowHelp(true); }}
            dayTargetMethod={dayTargetMethod}
            onDeleteDayTarget={handleDeleteDayTarget}
          />
        </div>
      )}
      <Space>
        <ConfigProvider locale={en_GB}>
          <Spin spinning={monthLoading}>
            <Calendar
              value={selectedDate}
              onChange={(date) => {
                handleDateChange(date);
                setSelectedDate(date);
              }}
              cellRender={cellRender}
              headerRender={headerRender}
              className={`custom-calendar calendar-${calendarId}`}
            />
          </Spin>
        </ConfigProvider>
      </Space>
      <HelpPopout showPopout={showHelp} setShowPopout={setShowHelp} />
      {showPredictionDialog && (
        <PredictionModal
          visible={showPredictionDialog}
          dateString={predictionDialogDate}
          setVisibility={setShowPredictionDialog}
          drillData={drills}
          onSuccess={handlePredictionSuccess}
          showDayTargetTabs
          method={dayTargetMethod}
        />
      )}
    </>
  );
};
